'use client';
import { type ModerationReportType } from '@shared/all/domains/moderation';
import {
  type PostSignupStep,
  SEEN_ACTIVATION_FUNNEL_KEY
} from '@shared/all/domains/onboarding';
import { type Product } from '@shared/all/domains/products';
import { patchChangesNothing } from '@shared/utils/context/reducers/sameValue';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { toast } from 'react-hot-toast';

// The app-wide modals (auth, moderation report, purchase, founders offer) and
// the confetti, in one reducer behind two contexts. The actions context never
// changes, so the many components that only open a modal (every post's vote,
// reply and menu buttons) never rerender when one opens. Only the widgets that
// render the modals read the state.

export type WidgetState = {
  loginModalOpen: boolean;
  signupModalOpen: boolean;
  // "Join factiii" opens the pre-auth funnel; the signup form slides in at its end.
  funnelOpen: boolean;
  authContinueModalOpen: boolean;
  logoutModalOpen: boolean;
  isAddAccountMode: boolean;
  // The post-signup flow is one modal that steps through its panels, so one
  // value says both whether it is open and where it is: 'verify' (bot-account
  // alert + verification prompt), 'founders' (the founders offer) or 'done'
  // ("you're set" plus the recovery-email offer). null = closed.
  postSignupStep: PostSignupStep | null;
  moderationReport: {
    type: ModerationReportType;
    target: string | number;
  } | null;
  purchaseModal: { isOpen: boolean; product: Product | null };
  foundersTokenModalOpen: boolean;
  confettiRunning: boolean;
};

const initialWidgetState: WidgetState = {
  loginModalOpen: false,
  signupModalOpen: false,
  funnelOpen: false,
  authContinueModalOpen: false,
  logoutModalOpen: false,
  isAddAccountMode: false,
  postSignupStep: null,
  moderationReport: null,
  purchaseModal: { isOpen: false, product: null },
  foundersTokenModalOpen: false,
  confettiRunning: false
};

const widgetReducer = (state: WidgetState, patch: Partial<WidgetState>) =>
  patchChangesNothing(state, patch) ? state : { ...state, ...patch };

// How long one throw of confetti runs.
const CONFETTI_MS = 5000;

const createWidgetActions = (
  dispatch: (patch: Partial<WidgetState>) => void
) => {
  let confettiTimer: ReturnType<typeof setTimeout> | null = null;

  return {
    setAuthContinueModalOpen: (open: boolean) =>
      dispatch({ authContinueModalOpen: open }),
    setLogoutModalOpen: (open: boolean) => dispatch({ logoutModalOpen: open }),
    setLoginModalOpen: (open: boolean) =>
      dispatch(
        open
          ? { loginModalOpen: true }
          : { loginModalOpen: false, isAddAccountMode: false }
      ),
    setSignupModalOpen: (open: boolean) => dispatch({ signupModalOpen: open }),
    setFunnelOpen: (open: boolean) => dispatch({ funnelOpen: open }),
    openAuthContinueModal: () => dispatch({ authContinueModalOpen: true }),
    openLogoutModal: () => dispatch({ logoutModalOpen: true }),
    openLoginModal: () => dispatch({ loginModalOpen: true }),
    // openSignupModal is the "Join factiii" entry: it shows the funnel first,
    // but once it's been seen on this device, skips straight to the signup
    // form. Same key onboardingStorage writes, imported from the one place
    // that names it.
    openSignupModal: () => {
      const seenFunnel =
        typeof window !== 'undefined' &&
        window.localStorage?.getItem(SEEN_ACTIVATION_FUNNEL_KEY) === '1';
      dispatch(seenFunnel ? { signupModalOpen: true } : { funnelOpen: true });
    },
    setPostSignupStep: (step: PostSignupStep | null) =>
      dispatch({ postSignupStep: step }),
    // Opens the post-signup flow at its first step; what every signup path calls.
    openPostSignup: () => dispatch({ postSignupStep: 'verify' }),
    openLoginForAddAccount: () =>
      dispatch({ isAddAccountMode: true, loginModalOpen: true }),

    newModerationReport: (
      type: ModerationReportType,
      target: string | number
    ) => dispatch({ moderationReport: { type, target } }),
    closeNewModerationReportModal: () => dispatch({ moderationReport: null }),
    setPurchaseModal: (isOpen: boolean, product: Product | null) =>
      dispatch({ purchaseModal: { isOpen, product } }),
    openFoundersTokenModal: () => dispatch({ foundersTokenModalOpen: true }),
    dismissFoundersTokenModal: () =>
      dispatch({ foundersTokenModalOpen: false }),

    // Runs the confetti for CONFETTI_MS; a second throw restarts the clock.
    throwConfetti: () => {
      dispatch({ confettiRunning: true });
      if (confettiTimer) clearTimeout(confettiTimer);
      confettiTimer = setTimeout(() => {
        confettiTimer = null;
        dispatch({ confettiRunning: false });
      }, CONFETTI_MS);
    }
  };
};

export type WidgetActions = ReturnType<typeof createWidgetActions>;

// Exported for tests, which provide their own values.
export const WidgetActionsContext = createContext<WidgetActions>(
  createWidgetActions(() => {})
);
export const WidgetStateContext =
  createContext<WidgetState>(initialWidgetState);

/** Opens and closes the auth modals. Never rerenders the caller. */
export const useAuthModals = () => useContext(WidgetActionsContext);

/**
 * Opens the moderation report, purchase and founders modals, and throws
 * confetti. Never rerenders the caller.
 */
export const useAppModals = () => useContext(WidgetActionsContext);

/** Which modals are open. Only the widgets that render them should read this. */
export const useWidgetState = () => useContext(WidgetStateContext);

type Props = PropsWithChildren<{
  // Tests start a modal open; the app always starts closed.
  initialState?: Partial<WidgetState>;
}>;

export const WidgetsProvider = ({ children, initialState }: Props) => {
  const [state, dispatch] = useReducer(widgetReducer, {
    ...initialWidgetState,
    ...initialState
  });
  const [actions] = useState(() => createWidgetActions(dispatch));

  // A landing from the welcome link or a donation's Stripe return throws
  // confetti once, then strips the parameter so a reload or a back
  // navigation does not throw it again. Read from window.location, not
  // useSearchParams: this provider sits in the web app's prerendered shell,
  // and a useSearchParams read there makes the root layout's Suspense
  // boundary a dynamic hole for every page (Cache Components).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const welcome = params.get('welcome') === 'true';
    const donation = params.get('donation') === 'success';
    if (!welcome && !donation) return;

    actions.throwConfetti();
    if (donation) toast.success('Thank you for your donation!');

    params.delete('welcome');
    params.delete('donation');
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
  }, []);

  return (
    <WidgetActionsContext.Provider value={actions}>
      <WidgetStateContext.Provider value={state}>
        {children}
      </WidgetStateContext.Provider>
    </WidgetActionsContext.Provider>
  );
};
